import { Inject, Injectable } from '@angular/core';
import { IDataService } from '../../data/data-service.interface';
import { RbacServiceInterface } from '../../rbac/rbac-service.interface';
import { UntypedFormControl, Validators } from '@angular/forms';
import { FieldModel } from './field.model';
import {
  TextFieldComponent,
  SelectComponent,
  BooleanSelectFieldComponent,
  DatepickerFieldNewComponent,
  TagsFieldComponent,
  SelectWithIconComponent,
  NumberFieldComponent,
} from '@platform500services/p500-ui-kit';
import { CountryRegionComponent } from '../../../components/fields/default/country-region/country-region.component';

@Injectable({
  providedIn: 'root',
})
export class FilterControlsFactory {
  private readonly globalData = this.dataService.getData();

  constructor(
    @Inject('IDataService') private dataService: IDataService,
    @Inject('RbacServiceInterface') public rbacService: RbacServiceInterface
  ) {}

  public getField(type: any, key: string, label?: string, formControl?: UntypedFormControl): FieldModel {
    const control = formControl ? formControl : new UntypedFormControl();

    switch (type) {
      case 'string':
        return new FieldModel(label, key, TextFieldComponent, control);

      case 'boolean':
        return new FieldModel(label, key, BooleanSelectFieldComponent, control);

      case 'boolean_switch':
        return new FieldModel(label, key, BooleanSelectFieldComponent, control, null, 'boolean_switch');

      case 'datetime':
        return new FieldModel(label, key, DatepickerFieldNewComponent, control);

      case 'finance_payment_status':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.finance_payment_statuses,
        });

      case 'finance_invoice_status':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.finance_invoice_statuses,
        });

      case 'finance_earning_status':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.finance_earning_statuses,
        });

      case 'country':
        return new FieldModel(label, key, SelectWithIconComponent, control, {
          multiple: true,
          data: this.globalData.countries,
        });

      case 'advertiser_id':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.advertisers,
        });

      case 'goal_type':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.goal_types,
        });

      case 'offer_id':
        return new FieldModel(label, key, SelectComponent, control, { multiple: true, data: this.globalData.offers });

      case 'goal_id':
        return new FieldModel(label, key, SelectComponent, control, { multiple: true, data: this.globalData.goals });

      case 'tracking_domain_id':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.tracking_domains,
        });

      case 'tag_numbers':
        return new FieldModel(label, key, TagsFieldComponent, control, { onlyNumbers: true });

      case 'number': {
        const numberModel = new FieldModel(label, key, NumberFieldComponent, control);
        numberModel.formControl.addValidators([Validators.max(2147483647), Validators.min(1)]);
        return numberModel;
      }

      case 'invoice_status':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.invoice_statuses,
        });

      case 'tag_strings':
        return new FieldModel(label, key, TagsFieldComponent, control);

      case 'payout_model_id':
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: this.globalData.payout_type,
          },
          type
        );

      case 'revenue_model_id':
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: this.globalData.revenue_type,
          },
          type
        );
      case 'country_region': {
        return new FieldModel($localize`:@@common.country_region:Country and Region`, key, CountryRegionComponent, control, {
          multiple: true,
          data: this.globalData.countriesWithoutAll,
        });
      }
    }

    if (type in this.globalData) {
      return new FieldModel(
        label,
        key,
        SelectComponent,
        control,
        {
          multiple: true,
          data: this.globalData[type],
        },
        type
      );
    }

    return new FieldModel(label, key, TextFieldComponent, control);
  }
}
